import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'
export type Density = 'comfortable' | 'compact'

export interface AccentOption {
  label: string
  ramp: Record<string, string>
}

// Full 50–900 ramps so every existing bg-brand-*/text-brand-*/border-brand-*
// usage across the app (buttons, active nav state, badges, focus rings,
// selection color) updates consistently when the accent changes — not just
// the swatch itself.
/** Studio Happens' own magenta. The fallback whenever a stored accent is unknown. */
export const DEFAULT_ACCENT = '#FC2265';

export const ACCENT_OPTIONS: Record<string, AccentOption> = {
  '#FC2265': {
    label: 'Pink',
    ramp: {
      50: '#fff0f4', 100: '#ffe0e9', 200: '#ffc2d3', 300: '#ff8fac', 400: '#fd5c86',
      500: '#FC2265', 600: '#e01050', 700: '#b90d41', 800: '#8f0a33', 900: '#6b0827',
    },
  },
  '#3b7dde': {
    label: 'Blue',
    ramp: {
      50: '#eaf1fd', 100: '#d5e3fb', 200: '#abc7f7', 300: '#82abf3', 400: '#5c92ea',
      500: '#3b7dde', 600: '#2c63b8', 700: '#1f4a8c', 800: '#173867', 900: '#112944',
    },
  },
  '#2f9e57': {
    label: 'Green',
    ramp: {
      50: '#edf7ee', 100: '#d6eeda', 200: '#aeddb6', 300: '#86cc92', 400: '#57b56d',
      500: '#2f9e57', 600: '#257f46', 700: '#1c6338', 800: '#154b2b', 900: '#0f351f',
    },
  },
  '#8a5cf5': {
    label: 'Purple',
    ramp: {
      50: '#f3effe', 100: '#e6ddfd', 200: '#cdbdfb', 300: '#b09af7', 400: '#9d7ff6',
      500: '#8a5cf5', 600: '#7440e0', 700: '#5c30b3', 800: '#452486', 900: '#301a5e',
    },
  },
}

const STORAGE_KEY = 'fieldnote.appearance'

interface StoredAppearance {
  theme: ThemePreference
  density: Density
  accentColor: string
}

function loadStored(): StoredAppearance {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return { theme: 'light', density: 'comfortable', accentColor: DEFAULT_ACCENT, ...JSON.parse(raw) }
  } catch {
    // ignore — fall through to defaults
  }
  return { theme: 'light', density: 'comfortable', accentColor: DEFAULT_ACCENT }
}

function applyToDom({ theme, density, accentColor }: StoredAppearance) {
  const root = document.documentElement
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)

  root.style.setProperty('--spacing', density === 'compact' ? '0.2rem' : '0.25rem')

  const ramp = ACCENT_OPTIONS[accentColor]?.ramp ?? ACCENT_OPTIONS[DEFAULT_ACCENT]?.ramp ?? {}
  for (const [shade, hex] of Object.entries(ramp)) {
    root.style.setProperty(`--color-brand-${shade}`, hex)
  }
}

interface AppearanceContextValue extends StoredAppearance {
  setTheme: (theme: ThemePreference) => void
  setDensity: (density: Density) => void
  setAccentColor: (color: string) => void
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null)

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<StoredAppearance>(loadStored)

  useEffect(() => {
    applyToDom(state)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch {
      // localStorage unavailable (private mode, etc.) — appearance just won't persist across reloads
    }
  }, [state])

  const value: AppearanceContextValue = {
    ...state,
    setTheme: (theme) => setState((prev) => ({ ...prev, theme })),
    setDensity: (density) => setState((prev) => ({ ...prev, density })),
    setAccentColor: (accentColor) => setState((prev) => ({ ...prev, accentColor })),
  }

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>
}

export function useAppearance() {
  const ctx = useContext(AppearanceContext)
  if (!ctx) throw new Error('useAppearance must be used within AppearanceProvider')
  return ctx
}
